// Custom Block Library — read-only service (Phase 1: curated blocks served from the app bundle).
//
// Phase 1 has NO user-publish path; curated blocks come from the trusted, gate-verified registry
// (src/engine/blockGate/curatedBlocks.ts). Phase 2 will additionally query custom_block_templates for
// user-published blocks and merge. Keeping Phase 1 pure (no Supabase) makes it fully CI-testable.

import { CURATED_BLOCKS, buildLibraryBlock, curatedTemplates, curatedVersion, findCurated } from '../engine/blockGate/curatedBlocks';
import { BlockType, type Block } from '../types';
import type { BlockCategory, CustomBlockSubtype, CustomBlockTemplate, CustomBlockVersion, CuratedBlock, LibraryEntry } from '../types/customBlockLibrary';

export interface LibraryFilters {
  category?: BlockCategory;
  blockType?: CustomBlockSubtype;
  query?: string;
}

/** List/search curated library blocks (head rows). */
export function listLibraryBlocks(filters?: LibraryFilters): CustomBlockTemplate[] {
  let out = curatedTemplates().filter((b) => b.publicVisible && b.status === 'published');
  if (filters?.category) out = out.filter((b) => b.category === filters.category);
  if (filters?.blockType) out = out.filter((b) => b.blockType === filters.blockType);
  if (filters?.query) {
    const q = filters.query.trim().toLowerCase();
    out = out.filter((b) => b.name.toLowerCase().includes(q) || b.description.toLowerCase().includes(q) || b.tags.some((t) => t.toLowerCase().includes(q)));
  }
  return out;
}

/** Distinct categories present (for the gallery facets). */
export function libraryCategories(): BlockCategory[] {
  return [...new Set(CURATED_BLOCKS.map((b) => b.category))];
}

/** Full curated block (for the detail drawer: description, params, examples, risk notes, code). */
export function getLibraryBlock(id: string): CuratedBlock | undefined { return findCurated(id); }

/** The code + parameter schema for a curated block version. */
export function getLibraryVersion(id: string): CustomBlockVersion | undefined { return curatedVersion(id); }

/** Build a CUSTOM Block to insert into a strategy, stamping provenance (libraryBlockId/libraryVersion). */
export function insertLibraryBlock(id: string, blockId?: string): Block | undefined {
  const c = findCurated(id);
  return c ? buildLibraryBlock(c, blockId) : undefined;
}

// ── Phase 2: unified entries (curated + user) ────────────────────────────────

/** Map a curated block to the unified LibraryEntry the gallery renders. */
function curatedToEntry(c: CuratedBlock): LibraryEntry {
  return {
    id: c.id, source: 'builtin', category: c.category, blockType: c.blockType, name: c.name,
    description: c.description, tags: c.tags, version: c.version, tier: c.tier, code: '', tslAst: c.tslAst,
    paramSchema: c.paramSchema, status: 'published', riskNotes: c.riskNotes, example: c.example,
  };
}

/** Curated blocks as unified entries (pure; the gallery merges these with public user blocks). */
export function curatedEntries(filters?: LibraryFilters): LibraryEntry[] {
  return listLibraryBlocks(filters).map((t) => curatedToEntry(findCurated(t.id)!));
}

/** Community library blocks a strategy references that are NO LONGER available (not in `availableIds`).
 *  Curated built-ins can't be disabled, so only librarySource==='user' refs are checked. Pure → testable. */
export function staleLibraryRefs(blocks: Block[], availableIds: Set<string>): Array<{ name: string; libraryBlockId: string }> {
  const seen = new Set<string>();
  const out: Array<{ name: string; libraryBlockId: string }> = [];
  for (const b of blocks) {
    const d = b.data as { libraryBlockId?: string; librarySource?: string; name?: string };
    if (d?.libraryBlockId && d.librarySource === 'user' && !availableIds.has(d.libraryBlockId) && !seen.has(d.libraryBlockId)) {
      seen.add(d.libraryBlockId);
      out.push({ name: d.name ?? 'custom block', libraryBlockId: d.libraryBlockId });
    }
  }
  return out;
}

/** Build a CUSTOM block from any library entry (curated or user), stamping full provenance. */
export function buildEntryBlock(entry: LibraryEntry, blockId?: string): Block {
  const id = blockId ?? `cb_${entry.id.replace(/[^a-z0-9]+/gi, '_')}_v${entry.version}`;
  const parameters: Record<string, unknown> = {};
  for (const [k, s] of Object.entries(entry.paramSchema)) parameters[k] = s.default;
  // TSL-only install contract: the engine executes library blocks ONLY from a declarative TSL AST. A
  // legacy entry that still carries host `code` without a tslAst would install as a quarantined no-op
  // (never executed) — refuse it explicitly instead of silently inserting a dead block.
  if (!entry.tslAst) {
    throw new Error(
      `library entry '${entry.id}' has no TSL AST — it predates the safe-TSL contract and cannot be installed. ` +
      'The author must republish it via the TSL Block Studio (host JavaScript is never executed).',
    );
  }
  const exec = { tslAst: entry.tslAst };
  return {
    id, type: BlockType.CUSTOM, position: { x: 100, y: 100 },
    data: {
      name: entry.name, blockType: entry.blockType, ...exec, parameters,
      libraryBlockId: entry.id, libraryVersion: entry.version,
      librarySource: entry.source, libraryAuthor: entry.author, libraryPublic: entry.tier.publicVisible,
    },
    connections: { inputs: [], outputs: [] },
  } as Block;
}

/**
 * Self-heal CURATED library blocks that lost their inline definition. A block inserted from the Block
 * Library references a curated block via `data.libraryBlockId` and should carry that block's `tslAst`
 * inline (buildEntryBlock attaches it). An older build could insert one WITHOUT the AST, leaving a block
 * with no code / no tslAst / no marker — which both fails validation and produces no signals at run time.
 * Re-attach the canonical curated AST (and fill any missing param defaults) so the block validates + runs.
 * Only touches builtin/curated refs we can resolve locally; everything else is returned unchanged.
 */
export function healCuratedLibraryBlocks(blocks: Block[]): Block[] {
  let changed = false;
  const out = blocks.map((b) => {
    if (b.type !== BlockType.CUSTOM) return b;
    const d = b.data as Record<string, unknown>;
    const libId = typeof d.libraryBlockId === 'string' ? d.libraryBlockId : '';
    const hasDef = !!d.tslAst
      || (typeof d.code === 'string' && d.code.trim().length > 0)
      || !!(d.parameters as { __canonical?: unknown } | undefined)?.__canonical;
    if (!libId || hasDef) return b;
    const cur = findCurated(libId);
    if (!cur?.tslAst) return b;
    const params = { ...((d.parameters as Record<string, unknown>) ?? {}) };
    for (const [k, s] of Object.entries(cur.paramSchema ?? {})) if (params[k] === undefined) params[k] = (s as { default?: unknown }).default;
    changed = true;
    return { ...b, data: { ...d, tslAst: cur.tslAst, parameters: params } } as Block;
  });
  return changed ? out : blocks;
}
